<template>
	<div
		class="container"
		:data-type="type"
		:data-direction="direction"
		:data-overflow="overflow"
		:style="style"
		:data-gap="gap"
		:data-clickable="clickable"
		:data-background="background"
		:data-separator="separator"
		:data-old-safari="isOldSafari()"
		:class="['gap-' + gap]"
	>
		<slot />
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import { CSSProperties } from "vue/types/jsx";
import BrowserVersion from "../utils/BrowserVersion";

const DIRECTION = ["row", "row-reverse", "column", "column-reverse"] as const;

const TYPE = [
	"inline",
	"inline-flex",
	"inline-grid",
	"inline-block",
	"block",
	"flex",
	"grid"
] as const;

const BACKGROUND = [
	"pop-over",
	"default",
	"element-light",
	"element",
	"element-dark",
	"font-dark",
	"font",
	"font-light",
	"icon-dark",
	"icon",
	"icon-light",
	"border-dark",
	"border",
	"border-light",
	"primary-dark",
	"primary",
	"primary-light",
	"secondary-dark",
	"secondary",
	"secondary-light",
	"error-dark",
	"error",
	"error-light",
	"warning-dark",
	"warning",
	"warning-light",
	"success-dark",
	"success",
	"success-light",
	"dashboard-background"
] as const;

type AlignTypeCommon = "start" | "center" | "space-between" | "stretch";
type AlignTypeX = AlignTypeCommon | ("left" | "right");
type AlignTypeY = AlignTypeCommon | ("top" | "bottom");

export type ContainerDirection = typeof DIRECTION[number];
export type ContainerType = typeof TYPE[number];
export type ContainerAlign = AlignTypeX | AlignTypeY | `${AlignTypeX} ${AlignTypeY}`;
export type ContainerOverflow = "hidden" | "auto" | "scroll" | "visible";
export type ContainerBackground = typeof BACKGROUND[number];

export default defineComponent({
	name: "Container",
	props: {
		/**
		 *
		 * Direction of child elements  <br/>
		 * 'row' | 'column' <br/><br/>
		 * Note : Works only for type='flex' and 'flex' is default type
		 */
		direction: {
			type: String as PropType<ContainerDirection>,
			default: "row",
			validator(value: ContainerDirection) {
				return DIRECTION.includes(value);
			}
		},

		/**
		 * 'inline' | 'inline-flex' | 'inline-grid' | 'inline-block' | 'block' | 'flex' | 'grid'
		 */
		type: {
			type: String as PropType<ContainerType>,
			default: "flex",
			validator(value: ContainerType) {
				return TYPE.includes(value);
			}
		},

		/**
		 *
		 * 	We can specify 2 values separated by space for horizontal and vertical alignment.<br/> Single value applies to both <br/>
		 *  'left' | 'right' | 'top' | 'bottom' | 'center' | 'left center' | 'center top' <br/><br/>
		 *
		 *  Note : Works only for type='flex' and 'flex' is default type
		 */
		align: {
			type: String as PropType<ContainerAlign>,
			default: "start center"
		},

		/**
		 * To override individual self alignment
		 *
		 * Note : Works only for type='flex' and 'flex' is default type
		 */
		alignSelf: {
			type: String,
			default: null
		},

		/**
		 * To assign flex-wrap property
		 *
		 * Note : Works only for type='flex' and 'flex' is default type
		 */

		flexWrap: {
			type: Boolean,
			default: false
		},

		/**
		 * How content overflow should work? <br/>
		 * 'hidden' | 'auto' | 'scroll' | 'visible'
		 */
		overflow: {
			type: String as PropType<ContainerOverflow>,
			default: "hidden"
		},

		/**
		 * Gap between items
		 */
		gap: {
			type: Number,
			default: 12
		},

		/**
		 * If Container is used inside flex container then This defines the ability for a flex item to shrink if necessary.
		 *
		 */
		shrink: {
			type: Number,
			default: 1
		},

		/**
		 * If Container is used inside flex container then This defines the ability for a flex item to grow if necessary.
		 *
		 */
		grow: {
			type: Number,
			default: 0
		},

		/**
		 * If Container is used inside flex container then This defines the initial size for a flex item.
		 *
		 */
		basis: {
			type: String,
			default: "auto"
		},

		/**
		 * Padding to set in container
		 */
		padding: {
			type: [Number, String],
			default: 12
		},

		/**
		 *
		 *  If single value specified then it is applied to for width and height. <br/>
		 * 	E.g '450px 300px' , '500px' <br/><br/>
		 * 	Note : Works only for type='grid'
		 */
		itemSize: {
			type: String,
			default: null
		},

		/**
		 * This will add hover effect and cursor-pointer
		 */
		clickable: {
			type: Boolean,
			default: false
		},

		/**
		 * 	Set background to wrapper <br/>
		 * 'pop-over' | 'default' | 'element-light' | 'element' | 'element-dark' | 'font-dark' | 'font' | 'font-light' | 'icon-dark' | 'icon' | 'icon-light' | 'border-dark' | 'border' | 'border-light' | 'primary-dark' | 'primary' | 'primary-light' | 'secondary-dark' | 'secondary' | 'secondary-light' | 'error-dark' | 'error' | 'error-light' | 'warning-dark' | 'warning' | 'warning-light' | 'success-dark' | 'success' | 'success-light' | 'dashboard-background'
		 */
		background: {
			type: String as PropType<ContainerBackground>,
			default: "default",
			validator(value: ContainerBackground) {
				return BACKGROUND.includes(value);
			}
		},

		/**
		 * Display separator betwwen childs <br/><br/>
		 *
		 * Note : Works only for type='flex' and 'flex' is default type
		 */
		separator: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		style() {
			const row = {
				left: "flex-start",
				right: "flex-end",
				top: "flex-start",
				bottom: "flex-end"
			};

			const column = {
				top: "flex-start",
				bottom: "flex-end",
				left: "flex-start",
				right: "flex-end"
			};

			let [horizontal, vertical] = this.align.split(" ");
			let calcPadding: string | number = `${this.padding}px`;

			//@ts-expect-error
			if (isNaN(this.padding)) {
				calcPadding = this.padding;
			}

			let style: CSSProperties = {
				gap: `${this.gap}px`,
				gridGap: `${this.gap}px`,
				flexShrink: this.shrink,
				flexGrow: this.grow,
				flexBasis: this.basis,
				padding: calcPadding,
				alignSelf: this.alignSelf
			};

			if (!vertical) {
				vertical = horizontal;
			}

			if (["row", "row-reverse"].indexOf(this.direction) > -1) {
				style = {
					...style,
					//@ts-expect-error
					justifyContent: row[horizontal] ? row[horizontal] : horizontal,
					//@ts-expect-error
					alignItems: column[vertical] ? column[vertical] : vertical
				};
			}

			if (["column", "column-reverse"].indexOf(this.direction) > -1) {
				style = {
					...style,
					//@ts-expect-error
					justifyContent: row[vertical] ? row[vertical] : vertical,
					//@ts-expect-error
					alignContent: column[horizontal] ? column[horizontal] : horizontal,
					alignItems: vertical === "center" && horizontal === "center" ? "center" : undefined
				};
			}

			if (this.type === "grid" && this.itemSize) {
				const [width, height] = this.itemSize.split(" ");
				style = {
					...style,
					gridTemplateColumns: `repeat(auto-fill,${width})`,
					gridTemplateRows: height ? `repeat(auto-fill,${height})` : "",
					gridAutoRows: height ? height : "1fr"
				};
			}

			if (this.flexWrap) {
				style = { ...style, flexWrap: "wrap" };
			}

			return style;
		}
	},
	methods: {
		isOldSafari() {
			// check safari less than 15 version
			return BrowserVersion.isOldSafari();
		}
	}
});
</script>
<style lang="scss">
@import "../styles/clickable";
$spacing: 12px;
$types: inline, inline-flex, inline-grid, inline-block, block, flex, grid;
$directions: row, row-reverse, column, column-reverse;
$overflows: hidden, auto, scroll, visible;
$backgrounds: (
	"pop-over": var(--slideout-background),
	"default": transparent,
	"element-light": var(--dashboard-element-light),
	"element": var(--dashboard-element),
	"element-dark": var(--dashboard-element-dark),
	"font-dark": var(--dashboard-font-dark),
	"font": var(--dashboard-font),
	"font-light": var(--dashboard-font-light),
	"icon-dark": var(--dashboard-icon-dark),
	"icon": var(--dashboard-icon),
	"icon-light": var(--dashboard-icon-light),
	"border-dark": var(--dashboard-border-dark),
	"border": var(--dashboard-border),
	"border-light": var(--dashboard-border-light),
	"primary-dark": var(--dashboard-primary-dark),
	"primary": var(--dashboard-primary),
	"primary-light": var(--dashboard-primary-light),
	"secondary-dark": var(--dashboard-secondary-dark),
	"secondary": var(--dashboard-secondary),
	"secondary-light": var(--dashboard-secondary-light),
	"error-dark": var(--dashboard-danger-dark),
	"error": var(--dashboard-danger),
	"error-light": var(--dashboard-danger-light),
	"warning-dark": var(--dashboard-warning-dark),
	"warning": var(--dashboard-warning),
	"warning-light": var(--dashboard-warning-light),
	"success-dark": var(--dashboard-success-dark),
	"success": var(--dashboard-success),
	"success-light": var(--dashboard-success-light),
	"dashboard-background": var(--dashboard-background)
);

div.container {
	grid-gap: $spacing;
	gap: $spacing;
	padding: $spacing;

	@each $type in $types {
		&[data-type="#{$type}"] {
			display: $type;
		}
	}
	@each $direction in $directions {
		&[data-direction="#{$direction}"][data-type="flex"] {
			flex-direction: $direction;
		}
	}

	@each $overflow in $overflows {
		&[data-overflow="#{$overflow}"] {
			overflow: $overflow;
		}
	}

	> .search-input-container {
		flex: 1;
	}

	&[data-type="block"] {
		> * + * {
			margin-top: $spacing;
		}
	}
	&[data-clickable] {
		@include clickable();
	}

	@each $name, $background in $backgrounds {
		&[data-background="#{$name}"] {
			background-color: $background;
		}
	}
	div.flow-tabs-container:not([no-content]) {
		flex: 1;
		align-self: stretch;
		height: inherit;
	}
	&[data-direction^="row"][data-type="flex"][data-separator] {
		> *:not(:only-child):not(:first-child) {
			border-left: 1px solid var(--dashboard-border-light);
		}
	}
	&[data-direction^="column"][data-type="flex"][data-separator] {
		> *:not(:only-child):not(:first-child) {
			border-top: 1px solid var(--dashboard-border-light);
		}
	}
	div.flow-radio-wrapper > div.flow-radio {
		margin-right: 0px;
	}
	div.flow-checkbox-wrapper > div > div.flow-checkbox {
		margin-right: 0px;
	}
	@media not all and (min-resolution: 0.001dpcm) {
		@supports (-webkit-appearance: none) {
			@for $i from 1 through 60 {
				&[data-direction="row"][data-old-safari][data-type="flex"].gap-#{$i} {
					> * + * {
						margin-left: $i + px;
					}
				}
				&[data-direction="column"][data-old-safari][data-type="flex"].gap-#{$i} {
					> * + * {
						margin-top: $i + px;
					}
				}
			}
		}
	}

	div.accordion-container > div.accordion-header > div.slab-hierarchy > div.slab-container {
		min-height: 0px;
	}

	div.accordion-container
		> div.accordion-body-wrapper
		> div.accordion-body
		> div.slab-hierarchy
		div.slab-container {
		min-height: 16px;
	}

	> table:only-child {
		width: 100%;
	}

	.dashboard-input {
		.flow-picto-container[state="default"][type="solid"],
		.search-input-container > .search-input-box > input,
		.search-input-container,
		div.multiselect > div.multiselect__tags > span.multiselect__single,
		div.multiselect > div.multiselect__tags > input.multiselect__input,
		div.multiselect > div.multiselect__tags,
		div.multiselect > div.multiselect__content-wrapper {
			background-color: var(--gray-500);
		}
		div.multiselect > div.multiselect__content-wrapper {
			border: 1px solid var(--gray-500);
		}
		div.multiselect
			> div.multiselect__content-wrapper
			> ul.multiselect__content
			> li.multiselect__element
			> span.multiselect__option {
			// padding: 0px;

			// &.multiselect__option--selected {
			// 	padding: 0px 8px 0px 0px;
			// }

			div.slab-hierarchy > div.slab-container > div.slab-content > div.slab-title {
				padding-left: 12px;
			}
		}
	}

	&.alert {
		position: relative;
		&:after {
			content: " ";
			position: absolute;
			top: 18px;
			left: 0px;
			height: 58px;
			border-right: 3px solid var(--dashboard-danger);
			border-top-right-radius: 3px;
			border-bottom-right-radius: 3px;
		}

		&.error-alert {
			&:after {
				border-right: 3px solid var(--dashboard-danger);
			}
		}
		&.warning-alert {
			&:after {
				border-right: 3px solid var(--dashboard-warning);
			}
		}
		&.success-alert {
			&:after {
				border-right: 3px solid var(--dashboard-success);
			}
		}
		&.primary-alert {
			&:after {
				border-right: 3px solid var(--dashboard-primary);
			}
		}
	}
}
</style>
